import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { backendURL, useApiFetch } from "../../utils/api";
import { Competition } from "../../types/Competition";
import { getShootingEventConfig } from "../../configs/utils/ShootingEventConfigFactory";
import { ShootingSession } from "../../types/ShootingSession";
import EventRankingTable from "../../components/EventRankingTable";
import { useNotifications } from "../../contexts/NotificationContext";
import "../../components/EventRankingTable.css";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

interface EventSessions {
    shootingEventId: string;
    shootingEventName: string;
    sessions: ShootingSession[];
}

function CompetitionReport() {
    const apiFetch = useApiFetch();
    const { competitionId } = useParams<{ competitionId: string }>();
    const navigate = useNavigate();
    const [competition, setCompetition] = useState<Competition | null>(null);
    const [eventSessionsList, setEventSessionsList] = useState<EventSessions[]>([]);

    const { info } = useNotifications();
    const HINT_KEY = "session-row-click-hint-seen";

    const { isConsentGiven, analyticsConsent } = useCookieConsent();

    useEffect(() => {
        if (!competitionId) {
            return;
        }

        const loadCompetition = async () => {
            try {
                const response = await apiFetch(`${backendURL}/competitions/${competitionId}`, {
                    method: "GET",
                    includeAuth: true
                });

                setCompetition(response);
            } catch (err) {
                console.error("Failed to load competition: ", err);
            }
        };

        loadCompetition();
    }, [competitionId]);

    useEffect(() => {
        if (analyticsConsent === "accepted" && competition) {
            ReactGA.event({
                category: "User",
                action: "Competition report viewed",
                label: competition.name
            });
        }
    }, [competition, analyticsConsent]);

    useEffect(() => {
        if (!competition) return;

        const loadAllEventSessions = async () => {
            const results: EventSessions[] = [];

            for (const shootingEventId of competition.shootingEventIds) {
                try {
                    const params = new URLSearchParams({
                        competitionId: competition.id,
                        shootingEventId: shootingEventId,
                    });
                    const sessions = await apiFetch(`${backendURL}/shooting-sessions/competition-sessions?${params}`, {
                        method: "GET",
                        includeAuth: true
                    });

                    const config = getShootingEventConfig(shootingEventId);
                    results.push({
                        shootingEventId,
                        shootingEventName: config.shootingEventName,
                        sessions,
                    });
                } catch (err) {
                    console.error(`Failed to load sessions for event ${shootingEventId}:`, err);
                }
            }

            setEventSessionsList(results);
        };

        loadAllEventSessions();
    }, [competition]);

    useEffect(() => {
        const hasSessions = eventSessionsList.some(e => e.sessions.length > 0);
        if (!hasSessions) return;

        if (!localStorage.getItem(HINT_KEY) && isConsentGiven) {
            info("Kliknij wiersz tabeli, aby otworzyć szczegóły sesji - rozkład trafień i statystyki", 0);
            localStorage.setItem(HINT_KEY, "1");
        }
    }, [eventSessionsList, isConsentGiven]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL');
    };

    return (
        <Container>
            <Row>
                <Col xl={8} md={10} xs={12} className="mx-auto">
                    {competition ? (
                        <div className="py-3">
                            <h1 className="font-weight-light text-center">
                                {competition.name} {formatDate(competition.date)}
                            </h1>

                            {eventSessionsList.map((eventSessions) => (
                                eventSessions.sessions.length > 0 &&
                                <div key={eventSessions.shootingEventId} className="mt-2">
                                    <h3 className="font-weight-light text-center mb-3">
                                        {eventSessions.shootingEventName}
                                    </h3>
                                    <EventRankingTable
                                        sessions={eventSessions.sessions}
                                        shootingEventId={eventSessions.shootingEventId}
                                        competitionName={competition.name}
                                        competitionDate={competition.date}
                                        maxEntries={3}
                                    />
                                    <div className="d-flex justify-content-center mt-0 mb-5">
                                        <Button
                                            variant="secondary"
                                            className="px-5"
                                            onClick={() => navigate(`/competition-reports/${competition.id}/${eventSessions.shootingEventId}`)}
                                        >
                                            Wyświetl pełne wyniki
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center mb-4">Brak danych</p>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

export default CompetitionReport;